import {
	LOGIN_REQUEST,
	LOGIN_SUCCESS,
	LOGIN_FAILURE,
	CLEAR_LOGIN_ERROR
} from "../../constants";

import { pharmacy_login_user } from '../../../networking/urlEndPoints'

import {
	fetchUserProfile,
	fetchPharmacyFileManagerUrl,
	getPharmacyAccountabilitySettings,
	getAfterProcessPrepCheckSetting
} from './userProfileActions'

import {
	createAction,
	actionTypes,
	CheckBookkeeperModuleAccess,
	CheckLogsExists
} from "../../reducers/pharmacy/other/commonReducerForInitalData";

import { fetchTimesheetDates } from "../main/pharmacyTimesheetDatesActions";
import { addUserActivityStatus } from "../chat/activityStatusActions";
import { fetchProvinceList } from "../patients/provinceListsActions";

import { authPharmacyPermissions } from "./authPermissions";
import tabId from '../../../utils/helper/tabID';

import Cookies from "js-cookie";
import Axios from '../../../networking/intraceptor'
import { primeMyModules } from '../../../hooks/useMyModules'
import { primeAdminStealth } from '../../../hooks/useAdminStealth'

import * as routeNames from '../../../routes/routeName'
import isMobileDevice from '../../../utils/isMobileDevice'

export const loginRequest = () => ({
	type: LOGIN_REQUEST,
});

export const loginSuccess = (user) => ({
	type: LOGIN_SUCCESS,
	payload: user,
});

export const loginFailure = (error) => ({
	type: LOGIN_FAILURE,
	payload: error,
});

export const clearLoginError = () => ({
	type: CLEAR_LOGIN_ERROR,
});

// Helper function to handle successful login (to avoid code duplication)
const handleSuccessfulLogin = async (dispatch, response, navigate) => {
	Cookies.set('authToken', response?.data?.data?.token)
	// Load the authoritative module list before opening the dashboard. This
	// prevents the header from first showing old login permissions and then
	// replacing them after the module request completes. Hard reload behaviour
	// remains unchanged because index.js still mounts immediately.
	await primeMyModules()
	// Same reason: resolve admin screen privacy (F9 x5) before the portal
	// renders, so a hidden admin's menus never appear and then disappear.
	await primeAdminStealth()
	const loginPharmacyData = response?.data?.data?.pharmacy?.[0]
	dispatch(loginSuccess(response?.data?.data));
	dispatch(authPharmacyPermissions())
	dispatch(fetchUserProfile())
	dispatch(fetchPharmacyFileManagerUrl())
	dispatch(fetchTimesheetDates())
	dispatch(fetchProvinceList())
	dispatch(addUserActivityStatus('online'));
	const settingsData = response?.data?.data?.settings
	dispatch(getPharmacyAccountabilitySettings(loginPharmacyData?.finalchk_at))
	dispatch(getAfterProcessPrepCheckSetting(settingsData?.pre_after_process ?? false))
	dispatch(createAction(actionTypes.UPDATE_LABEL_TO_MARGIN.SUCCESS, {
		data: loginPharmacyData?.label_top_margin,
		requestType: 'updateLabelToMargin'
	}))
	dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_HEADER.SUCCESS, {
		data: !!loginPharmacyData?.label_print_header,
		requestType: 'updateLabelPrintHeader'
	}))
	dispatch(createAction(actionTypes.UPDATE_LABEL_HEADER_TOP_MARGIN.SUCCESS, {
		data: loginPharmacyData?.label_header_top_margin ?? 0,
		requestType: 'updateLabelHeaderTopMargin'
	}))
	dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_TYPE.SUCCESS, {
		data: loginPharmacyData?.label_print_type || 'regular',
		requestType: 'updateLabelPrintType'
	}))
	dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_LOGO.SUCCESS, {
		data: !!loginPharmacyData?.label_print_logo,
		requestType: 'updateLabelPrintLogo'
	}))
	dispatch(createAction(actionTypes.UPDATE_LABEL_LEFT_MARGIN.SUCCESS, {
		data: loginPharmacyData?.label_left_margin ?? 0,
		requestType: 'updateLabelLeftMargin'
	}))
	dispatch(createAction(actionTypes.UPDATE_LABEL_SIG_GAP.SUCCESS, {
		data: loginPharmacyData?.label_sig_gap ?? 0,
		requestType: 'updateLabelSigGap'
	}))
	// Check bookkeeper module access on login success
	dispatch(CheckBookkeeperModuleAccess({ fetchType: 'checkBookkeeperModuleAccess' }))
	// Check logs exists on login success
	dispatch(CheckLogsExists({ fetchType: 'checkLogsExists' }))
	// ✅ After loginSuccess dispatch
	const user = response?.data?.data;
	if (typeof BroadcastChannel !== 'undefined') {
		const channel = new BroadcastChannel('auth-channel');
		channel.postMessage({
			type: 'login', tabId: tabId,
			response: user,
			loginPharmacyData: loginPharmacyData,
			settingsData: settingsData
		});
		channel.close();
	}
	const redirectPath = sessionStorage.getItem('postLoginRedirectPath')
	if (redirectPath === routeNames.pharmacy_accountability_upload_pics) {
		if (isMobileDevice()) {
			navigate(redirectPath)
		} else {
			navigate(routeNames.DASHBOARD_ROUTE)
		}
	} else if (redirectPath) {
		navigate(redirectPath)
	} else {
		navigate(routeNames.DASHBOARD_ROUTE)
	}
	// sessionStorage.removeItem('postLoginRedirectPath')
}

// Helper function to build request payload based on login step
const buildLoginPayload = (email, password, masterOtp, ipOtp, otpStage, ipStage) => {
	const payload = {
		email,
		password,
	};

	// Add Master OTP if provided (use 'otp' field and include otp_stage)
	if (masterOtp && otpStage) {
		payload.otp = masterOtp;
		payload.otp_stage = otpStage;
	}

	// Add IP OTP if provided (use 'ip_otp' field and include ip_stage)
	if (ipOtp) {
		payload.ip_otp = ipOtp;
		if (ipStage) {
			payload.ip_stage = ipStage;
		}
	}

	return payload;
};

// Helper function to handle OTP required response
const handleOtpRequiredResponse = (dispatch, response, otpStage) => {
	const responseData = response?.data?.data;
	
	// Check if IP OTP is required after Master OTP verification
	if (responseData?.requires_ip_otp === true) {
		dispatch(loginFailure({
			status: 200,
			data: {
				message: responseData?.message || response?.data?.message || 'Please verify the first OTP and IP OTP to continue.',
				requires_ip_otp: true,
				requires_master_otp: false, // Master OTP already verified
				otp_stage: otpStage,
				ip_stage: responseData?.ip_stage, // Store ip_stage from API response
				master_otp_verified: true
			}
		}));
		return true; // Indicates IP OTP required, login not completed
	}
	
	// Both auth_phone and user_phone should show Master OTP field
	if (otpStage === 'auth_phone' || otpStage === 'user_phone') {
		dispatch(loginFailure({
			status: 200,
			data: {
				message: responseData?.message || response?.data?.message || 'OTP has been sent. Please verify to continue.',
				requires_master_otp: true,
				otp_stage: otpStage,
				master_otp_sent: true
			}
		}));
		return true; // Indicates OTP required, login not completed
	}
	return false;
};

// Helper function to handle invalid Master OTP error
const handleInvalidMasterOtpError = (dispatch, error, otpStage) => {
	dispatch(loginFailure({
		status: 400,
		data: {
			message: error?.response?.data?.message || error?.response?.data?.data?.message || 'Invalid Master OTP',
			requires_master_otp: true,
			otp_stage: otpStage || 'auth_phone'
		}
	}));
};

// Unified login function - handles all login scenarios (password, Master OTP, IP OTP)
export const authLogin = (email, password, navigate, masterOtp = null, ipOtp = null, otpStage = null, ipStage = null) => {
	return async (dispatch) => {
		dispatch(loginRequest());
		try {
			const requestBody = buildLoginPayload(email, password, masterOtp, ipOtp, otpStage, ipStage);
			const response = await Axios.post(`${pharmacy_login_user}`, requestBody);

			if (response?.status === 200) {
				// Check if API returned success: false (error response with 200 status)
				if (response?.data?.success === false) {
					const errorMessage = response?.data?.message || 'An error occurred';
					// Handle invalid OTP errors
					if (errorMessage?.toLowerCase().includes('invalid otp')) {
						// Determine if it's Master OTP or IP OTP based on what was sent
						if (masterOtp && ipOtp) {
							// Both OTPs sent - likely IP OTP is invalid
							dispatch(loginFailure({
								status: 400,
								data: {
									message: errorMessage,
									requires_ip_otp: true,
									requires_master_otp: false,
									master_otp_verified: true
								}
							}));
						} else if (masterOtp && !ipOtp) {
							// Only Master OTP sent - Master OTP is invalid
							dispatch(loginFailure({
								status: 400,
								data: {
									message: errorMessage,
									requires_master_otp: true,
									otp_stage: otpStage || 'auth_phone'
								}
							}));
						} else {
							// Generic invalid OTP
							dispatch(loginFailure({
								status: 400,
								data: {
									message: errorMessage
								}
							}));
						}
						return;
					}
					// Other errors with success: false
					dispatch(loginFailure({
						status: 400,
						data: {
							message: errorMessage
						}
					}));
					return;
				}

				// Check if OTP is required based on new API structure
				if (response?.data?.success === true && response?.data?.data?.otp_required === true) {
					const responseOtpStage = response?.data?.data?.otp_stage;
					const otpRequired = handleOtpRequiredResponse(dispatch, response, responseOtpStage);
					if (otpRequired) return;
				}

				// Direct login success (token present)
				if (response?.data?.data?.token) {
					await handleSuccessfulLogin(dispatch, response, navigate);
				} else {
					throw new Error(response);
				}
			} else {
				throw new Error(response);
			}
		} catch (error) {
			if (error?.response) {
				const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message;

				// Handle invalid Master OTP (status 400 when only Master OTP is sent)
				if (error?.response?.status === 400 && masterOtp && !ipOtp) {
					handleInvalidMasterOtpError(dispatch, error, otpStage);
				}
				// Handle invalid IP OTP (status 400 when both Master OTP and IP OTP are sent)
				else if (error?.response?.status === 400 && masterOtp && ipOtp && errorMessage?.toLowerCase().includes('invalid otp')) {
					dispatch(loginFailure({
						status: 400,
						data: {
							message: errorMessage || 'Invalid OTP',
							requires_ip_otp: true,
							requires_master_otp: false,
							master_otp_verified: true
						}
					}));
				} else {
					// Handle other errors (including invalid credentials, etc.)
					dispatch(loginFailure(error?.response));
				}
			} else {
				dispatch(loginFailure({
					status: 0,
					data: {
						message: error?.message || 'Unable to reach the backend. Please try again.'
					}
				}));
			}
		}
	}
}
